import { Badge, Box } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import { ActiveMarkerIcon } from "src/components/active-marker-icon";
import { Icon } from "src/components/layout/nav/icons/agents";
import { NavItem } from "src/components/layout/nav/nav-item";
import { NavSection } from "src/components/layout/nav/nav-section";
import type { ActiveSectionProps } from "src/components/layout/nav/utils";
import { useShowAgentsInNav } from "src/components/layout/nav/sections/hooks/agents";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import {
  CALENDAR_PATH,
  useCalendarReadyNotice,
} from "src/pages/agents/calendar/setup/calendar-ready-notice";
import { useShouldShowAgentsTos } from "src/pages/agents/hooks/use-should-show-agents-tos";

import type { UseSectionHook } from "./types";
import { useReportTerm } from "src/pages/agents/reports/terms";

const CALENDAR_READY_LABEL = "Calendar is ready";

export const useAgentsSection: UseSectionHook = () => {
  const reportTerm = useReportTerm();
  const isEnabled = useAgentsEnabledFlag();
  const { shouldShowAgentsTos } = useShouldShowAgentsTos();
  const { agenticInitiatives, campaignCalendar } = useFlags();
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const isCalendarReady = useCalendarReadyNotice();
  const rightElement =
    isEnabled && shouldShowAgentsTos ? (
      <Badge size="sm" variant="info">
        Try now
      </Badge>
    ) : undefined;

  // For users who are not flagged into agents or legacy copilot, we'll just show a splash page on /agents.
  if (!isEnabled) {
    return {
      title: "Agents",
      rightElement,
      isEnabled: true,
      links: [
        {
          href: "/agents",
          label: "Agents",
        },
      ],
    };
  }

  return {
    title: "Agents",
    rightElement,
    markerLabel: isCalendarReady ? CALENDAR_READY_LABEL : undefined,
    isEnabled: true,
    links: [
      {
        isSub: true,
        href: "/agents/chats",
        label: "Chats",
      },
      {
        isSub: true,
        href: "/agents/reports",
        label: reportTerm.Plural,
      },
      {
        isSub: true,
        href: "/agents/custom-agents",
        label: "Custom agents",
      },
      ...(campaignCalendar
        ? [
            {
              isSub: true as const,
              href: CALENDAR_PATH,
              label: "Calendar",
              rightElement: isCalendarReady ? (
                <Box
                  as={ActiveMarkerIcon}
                  role="img"
                  aria-label={CALENDAR_READY_LABEL}
                  boxSize={2}
                  color="primary.base"
                />
              ) : undefined,
            },
          ]
        : []),
      ...(agenticInitiatives
        ? [
            {
              isSub: true as const,
              href: "/agents/initiatives",
              label: "Initiatives",
            },
          ]
        : []),
      ...(canConfigureAgents
        ? [
            {
              isSub: true as const,
              href: "/agents/settings",
              label: "Settings",
            },
          ]
        : []),
    ],
  };
};

export const Agents = (props: ActiveSectionProps) => {
  const section = useAgentsSection();
  const isEnabled = useAgentsEnabledFlag();
  const showAgents = useShowAgentsInNav();

  if (!showAgents) {
    return null;
  }

  // Just show a single nav item for the agents section if the user is not flagged into agents or legacy copilot.
  if (!isEnabled) {
    return (
      <NavItem
        href="/agents"
        icon={Icon}
        label="Agents"
        rightElement={section.rightElement}
      />
    );
  }

  return <NavSection icon={Icon} {...section} {...props} />;
};
